import {
  Charge,
  CreatePaymentRequest,
  CreatePaymentResponse,
  PaymentsService,
} from '@/@types/generated'
import { useError } from '@/hooks/useError'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useCharges } from './charges-provider'

interface PaymentProcessContextProps {
  readonly outstandingCharges: Charge[]
  readonly outstandingTotal: number
  readonly selectedCharges: Charge[]
  readonly selectedTotal: number
  // Key: charge ID, Value: amount to pay
  readonly paymentAmounts: Record<string, number>

  selectCharges: (charges: Charge[]) => void
  setPaymentAmount: (chargeID: string, amount: number) => void
  submit: (
    req: Omit<CreatePaymentRequest, 'appliedCharges'>
  ) => Promise<CreatePaymentResponse>
  reset: () => void
}

const PaymentProcessContext = createContext<PaymentProcessContextProps>(
  undefined!
)

export function PaymentProcessProvider({ children }: React.PropsWithChildren) {
  // Hooks
  const { data: charges } = useCharges()
  const { showError } = useError()

  // State
  const [selected, setSelected] = useState<Charge[]>([])
  const [paymentAmounts, setPaymentAmounts] = useState<Record<string, number>>(
    {}
  )

  // Submit the payment to be processed — redirects to SnapPay hosted page
  const submit = useCallback(
    async (req: Omit<CreatePaymentRequest, 'appliedCharges'>) => {
      return await PaymentsService.createPayment({
        ...req,
        appliedCharges: Object.entries(paymentAmounts).map(([id, amount]) => ({
          id,
          amount,
        })),
      })
    },
    [paymentAmounts]
  )

  // Reset the payment process
  const reset = useCallback(() => {
    // Reset
    setSelected([])
    setPaymentAmounts({})
  }, [])

  // Select charges wrapper that validates business entity constraint
  const _selectCharges = useCallback(
    (v: Charge[]) => {
      // Ensure multiple charges cannot cross business entities
      const multipleCompanies = v.map(
        ({ businessEntityId }) => businessEntityId
      )
      const uniqueCompanies = Array.from(new Set(multipleCompanies))
      if (uniqueCompanies.length > 1) {
        showError('Cannot select charges for multiple companies.')
        throw new Error('Cannot select charges from multiple companies.')
      }

      setSelected(v)
    },
    [showError]
  )

  // Selected charges changed, clear out any orphaned payment amounts
  useEffect(() => {
    setPaymentAmounts((prev) =>
      // Only keep amounts for selected charges
      selected.reduce<Record<string, number>>((acc, { id }) => {
        if (prev[id] !== undefined) acc[id] = prev[id]
        return acc
      }, {})
    )
  }, [selected])

  // Computed
  const total = useCallback(
    (c: Charge[]) =>
      c.reduce((sum, { unpaidAmount }) => sum + unpaidAmount, 0) || 0,
    []
  )

  return (
    <PaymentProcessContext.Provider
      value={{
        outstandingCharges: charges,
        outstandingTotal: total(charges),
        selectedCharges: selected,
        selectedTotal: total(selected),
        paymentAmounts,
        selectCharges: _selectCharges,
        setPaymentAmount: (chargeID: string, amount: number) => {
          setPaymentAmounts((prev) => ({
            ...prev,
            [chargeID]: amount,
          }))
        },
        submit,
        reset,
      }}
    >
      {children}
    </PaymentProcessContext.Provider>
  )
}

export function usePaymentProcess() {
  const context = useContext(PaymentProcessContext)
  if (context === undefined)
    throw new Error(
      'usePaymentProcess must be used within a PaymentProcessProvider'
    )

  return context
}
